/* global TPDirect */
import React, {
  useCallback, useEffect, useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle as MuiDialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  withStyles,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import moment from 'moment';
import CountTextField from '../../preorder/components/CountTextField';
import { PAYMENT_METHOD, PAYMENT_INPUT_STYLE, setupTappayFields } from '../../preorder/containers/CheckoutPage/components/Checkout';
import Alert from '../../preorder/components/Alert';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

export const OrderButton = withStyles(({
  root: {
    color: '#ffffff',
    borderRadius: 8,
    padding: '8px 4px',
    backgroundColor: '#e08916',
    height: '100%',
    '&:hover': {
      backgroundColor: '#e08916',
    },
  },
  text: {
    padding: 0,
  },
}))(Button);

const DialogTitle = withStyles(styles)((props) => {
  const {
    children, classes, onClose, ...other
  } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const StyledDialog = withStyles({
  paper: {
    backgroundColor: '#f2f2f2',
  },
})(Dialog);

const StyledRadio = withStyles(() => ({
  root: {
    '&$checked': {
      color: '#009687',
    },
  },
  checked: {},
}))(Radio);

const CreditCardField = () => {
  useEffect(() => {
    setupTappayFields();
  }, []);

  return (
    <>
      <Box
        fontSize="body2.fontSize"
        fontWeight="fontWeightBold"
        color="grey.700"
      >
        您的信用卡號
      </Box>
      <Box mt={1} mb={2}>
        <div
          id="card-number"
          style={PAYMENT_INPUT_STYLE}
        />
      </Box>
      <Box
        fontSize="body2.fontSize"
        fontWeight="fontWeightBold"
        color="grey.700"
      >
        卡片有效期限
      </Box>
      <Box mt={1} mb={2}>
        <div
          id="card-expiration-date"
          style={PAYMENT_INPUT_STYLE}
        />
      </Box>
      <Box
        fontSize="body2.fontSize"
        fontWeight="fontWeightBold"
        color="grey.700"
      >
        卡片背面末3碼
      </Box>
      <Box mt={1} mb={2}>
        <div id="card-ccv" style={PAYMENT_INPUT_STYLE} />
      </Box>
    </>
  );
};

export default ({
  data,
  isOpen,
  onClose,
  onOrderButtonClick: propsOnOrderButtonClick,
}) => {
  const [alert, setAlert] = React.useState(null);
  const [buyCount, setBuyCount] = useState(1);
  const { product, fridge, redeemStartTime } = data || {};
  const { name: fridgeName, id: fridgeId, address: fridgeAddress } = fridge || {};
  const {
    coverImage,
    id: productId,
    name: productName,
    price,
    provider: providerName,
  } = product || {};
  const [totalPrice, setTotalPrice] = useState(0);
  const [paymentMethod, setPaymentMethod] = React.useState(PAYMENT_METHOD.TAP_PAY);

  useEffect(() => {
    if (buyCount && price) setTotalPrice(buyCount * price);
  }, [buyCount, price]);

  const onOrderButtonClick = useCallback(async () => {
    try {
      if (paymentMethod === PAYMENT_METHOD.TAP_PAY) {
        TPDirect.card.getPrime(async ({ status = -1, card = {} }) => {
          if (status < 0) throw new Error('您輸入的信用卡資訊有錯誤，請重新輸入！');

          await propsOnOrderButtonClick({
            totalPrice,
            paymentMethod,
            paymentInfo: {
              cardNumber: `${card?.bincode}******${card?.lastfour}`,
              cardToken: card?.prime,
              cardType: card?.type,
            },
            items: [{
              buyCount,
              coverImage,
              fridgeId,
              price,
              productId,
              productName,
              providerName,
              redeemStartTime,
            }],
          });
        });
      } else {
        await propsOnOrderButtonClick({
          totalPrice,
          paymentMethod,
          items: [{
            buyCount,
            coverImage,
            fridgeId,
            price,
            productId,
            productName,
            providerName,
            redeemStartTime,
          }],
        });
      }
    } catch (e) {
      setAlert({
        title: '交易失敗',
        message: e.message,
      });
    }
  }, [
    coverImage,
    paymentMethod,
    fridgeId,
    productId,
    buyCount,
    totalPrice,
    price,
    productName,
    propsOnOrderButtonClick,
    providerName,
    redeemStartTime,
  ]);

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle onClose={onClose}> </DialogTitle>
      <DialogContent>
        <Box fontWeight="bold" px={{ sm: 7 }}>
          【
          {providerName}
          】
          {productName}
        </Box>
        <Box fontWeight="bold" px={{ sm: 7 }} mt={2}>
          <Grid container direction="column" spacing={2}>
            <Grid container item direction="row" alignItems="center">
              <Grid item xs={4}>
                <Box>訂購數量</Box>
              </Grid>
              <Grid item xs={8}>
                <CountTextField
                  defaultValue={buyCount}
                  value={buyCount}
                  minValue={1}
                  onChange={setBuyCount}
                />
              </Grid>
            </Grid>
            <Grid container item direction="row" alignItems="center">
              <Grid item xs={4}>
                總計金額
              </Grid>
              <Grid item xs={8}>
                {totalPrice}
              </Grid>
            </Grid>
            <Grid container item direction="row" alignItems="center">
              <Grid item xs={4}>
                取貨時間
              </Grid>
              <Grid item xs={8}>
                <Box component="span">
                  {redeemStartTime
                  && moment(redeemStartTime).hour(11).minute(30)
                    .format('YYYY/MM/DD HH:mm')}
                </Box>
                <Box component="span"> ~ </Box>
                <Box component="span">
                  {redeemStartTime
                  && moment(redeemStartTime).hour(14).minute(30)
                    .format('HH:mm')}
                </Box>
              </Grid>
            </Grid>
            <Grid container item direction="row">
              <Grid item xs={4}>
                取貨地點
              </Grid>
              <Grid item xs={8}>
                <Box>{fridgeName}</Box>
                <Box fontSize={14} color="#535353">
                  地址：
                  {fridgeAddress}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Box
            mt={4}
            mb={2}
            fontSize="subtitle1.fontSize"
            fontWeight="fontWeightBold"
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12}>
                <Box color="grey.700">付款方式：</Box>
              </Grid>
              <Grid item xs={12}>
                <RadioGroup
                  value={paymentMethod}
                  onChange={(event) => setPaymentMethod(event.target.value)}
                >
                  <FormControlLabel value={PAYMENT_METHOD.LINE_PAY} control={<StyledRadio />} label="以 LINE Pay 付款" />
                  <FormControlLabel value={PAYMENT_METHOD.TAP_PAY} control={<StyledRadio />} label="以 信用卡 付款" />
                </RadioGroup>
              </Grid>
            </Grid>
          </Box>
          {
            paymentMethod === PAYMENT_METHOD.TAP_PAY && (
            <Box my={2}>
              <Grid container>
                <Grid item xs={12}>
                  <CreditCardField />
                </Grid>
              </Grid>
            </Box>
            )
          }
          <Box mt={4}>
            <Box>【取貨流程說明】</Box>
            <Box>1. 訂購商品上架當日將會收到Line通知</Box>
            <Box>2. 請於收到通知後前往指定取貨地點</Box>
            <Box>3. 請依照一般開門流程，領取標有您「手機末三碼」的商品</Box>
            <Box mt={2}>【注意事項】</Box>
            <Box>
              1. 請務必於
              <Box component="span" fontWeight="bold">規定之取貨時段內完成取貨</Box>
              。
            </Box>
            <Box>2. 如未取貨，商品將下架，基於食品安全考量將無法再售出。因此恕不接受取消訂單與退款，訂購前請務必確認。</Box>
            <Box>3. 訂購商品恕無法適用任一優惠活動與優惠券折抵，請特別注意。</Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box height={44} width={1} px={{ sm: 7 }} mb={{ sm: 4 }}>
          <OrderButton onClick={onOrderButtonClick} fullWidth>
            訂購
          </OrderButton>
        </Box>
      </DialogActions>
      {
        alert && (
          <Alert
            isOpen
            title={alert?.title || ''}
            content={alert?.message || ''}
            buttons={alert?.buttons || (
              <Button fullWidth onClick={() => setAlert(null)}>
                <Box fontWeight="fontWeightBold" color="support.main">
                  確認
                </Box>
              </Button>
            )}
          />
        )
      }
    </StyledDialog>
  );
};
